import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { LoaderService, SessionManagerService, ToastService } from 'src/app/shared/services';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { ToolbarService } from '@app/features/student/canvas-dashboard/services/toolbar.service';
import { TranslateService } from '@ngx-translate/core';
import { isTouchSafari } from '@app/shared/helper/platform';
declare const google: any;

// Matches the token's ~1 hour lifetime, renewing just before it lapses.
const TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000;
// A pop-up that iPadOS defers never calls back at all, so release the
// single-request guard eventually or the user could never retry.
const REQUEST_TIMEOUT_MS = 90 * 1000;

/**
 * Owns the Google access token: when it is renewed, and by what means.
 *
 * Renewal always opens a Google pop-up. Where a browser permits that silently
 * — desktop and Chromebook, with a signed-in Google session — it is done
 * automatically and the user sees nothing. On iPad it cannot be: iPadOS defers
 * the window past the gesture that opened it, leaving a tab stuck on Google's
 * loading spinner. There the user is asked to tap instead, and that tap is what
 * opens the pop-up.
 *
 * Refreshes only ever run while the tab is visible. Refreshing in the
 * background is what used to throw Google windows at people who were not even
 * using the app.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleAccessTokenService {
  public client: any;
  isGoogleTokenValid = false;
  private tokenValiditySubject = new BehaviorSubject<boolean>(false);
  tokenValidity$ = this.tokenValiditySubject.asObservable();

  // True when the Google token has expired and only a user tap can renew it.
  private reauthNeededSubject = new BehaviorSubject<boolean>(false);
  reauthNeeded$ = this.reauthNeededSubject.asObservable();
  private isInitialized = false;
  private gisScriptPromise: Promise<void> | undefined;
  // Guards against two pop-ups being opened at once (e.g. two visibility
  // listeners firing on the same wake).
  private requestInFlight = false;
  private requestInFlightTimeoutId: any;
  // Work waiting on a fresh token, e.g. "save to Drive". A list, so a tap that
  // lands while a renewal is already running queues rather than being dropped.
  private pendingActions: Array<() => void> = [];
  private refreshIntervalId: any;
  googleAuthFailCallback: any;

  constructor(
    private sessionManagerService: SessionManagerService,
    private flashMessage: ToastService,
    private loader: LoaderService,
    private router: Router,
    private injector: Injector,
    private translateService: TranslateService,
  ) {
    this.loadGISScript().then(() => {
      this.initializeAuth();
    });
    this.startRefreshInterval();
  }

  /** Loads the GIS script once; repeat callers share the same promise. */
  public loadGISScript(): Promise<void> {
    if (this.gisScriptPromise) {
      return this.gisScriptPromise;
    }

    this.gisScriptPromise = new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Allow a later attempt to retry the load.
        this.gisScriptPromise = undefined;
        reject(new Error('Google API script could not be loaded.'));
      };
      document.head.appendChild(script);
    });

    return this.gisScriptPromise;
  }

  initializeAuth() {
    // Build the token client once. Re-creating it on every expiry check leaked
    // clients and left stale popup handlers behind.
    if (this.isInitialized && this.client) {
      return;
    }
    if (typeof google === 'undefined' || !google?.accounts?.oauth2) {
      return;
    }

    this.client = google.accounts.oauth2.initTokenClient({
      client_id: environment.google.client_id,
      prompt: '',
      scope:
        'openid email profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/classroom.coursework.students.readonly https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.coursework.me.readonly https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.install',
      callback: async (response: { access_token: any; expires_in: any }) => {
        this.endRequest();
        try {
          const IsgoogleMailUser = this.sessionManagerService.getUserEmail();
          const userProfileInfo = await fetch('https://www.googleapis.com/oauth2/v1/userinfo', {
            headers: { Authorization: `Bearer ${response.access_token}` },
          });
          const userIntegratedProfileData = await userProfileInfo.json();

          if (userIntegratedProfileData.email == IsgoogleMailUser) {
            const currentTimestamp = Date.now();
            const expiresInSeconds = Number(response.expires_in) || 3599;
            const updatedTimestampWithTokenExpiry = currentTimestamp + expiresInSeconds * 1000;
            localStorage.setItem('lastGeneratedAccessTokenExpires', `${updatedTimestampWithTokenExpiry}`);
            this.sessionManagerService.setAccessToken(response.access_token);
            this.isInitialized = true;
            this.setReauthNeeded(false);
            this.tokenValiditySubject.next(true);
            this.runPendingActions();
          } else {
            const toolbar = this.injector.get(ToolbarService);
            localStorage.setItem('logout', 'true');
            toolbar.logout();

            this.translateService.get('ERROR.pickedWrongUser').subscribe((translatedMessage: string) => {
              this.flashMessage.showError(translatedMessage);
            });
            // Trigger the failure callback if it exists
            if (this.googleAuthFailCallback) {
              this.googleAuthFailCallback();
              this.googleAuthFailCallback = null;
            }
          }
        } finally {
          // Never leave the app stuck behind a spinner.
          this.loader.stop();
        }
      },
      error_callback: (err: any) => {
        this.endRequest();
        this.clearPendingActions();
        this.loader.stop();

        const url = this.getCurrentUrl();
        if (url.includes('nyc/callback')) {
          const toolbarService = this.injector.get(ToolbarService);
          toolbarService.logout();

          if (this.googleAuthFailCallback) {
            this.googleAuthFailCallback();
            this.googleAuthFailCallback = null;
          }
          if (err?.message !== 'Popup window closed') {
            this.translateService.get('ERROR.popupPermissionMsg').subscribe((translatedMessage: string) => {
              this.flashMessage.showError(translatedMessage);
            });
          }
          return;
        }

        this.setReauthNeeded(true);
        this.tokenValiditySubject.next(false);

        // Release anything waiting on this token (e.g. the login flow), so a
        // refused popup can never strand the user mid-navigation.
        if (this.googleAuthFailCallback) {
          this.googleAuthFailCallback();
          this.googleAuthFailCallback = null;
        }

        const type = err?.type;
        if (type === 'popup_closed') {
          // The user dismissed the Google window; not an error worth a toast.
          return;
        }

        this.translateService.get('ERROR.popupPermissionMsg').subscribe((translatedMessage: string) => {
          this.flashMessage.showError(translatedMessage);
        });
      },
    });
    this.isInitialized = true;
  }

  onGoogleAuthFail(callback: any) {
    this.googleAuthFailCallback = callback;
  }

  isAuthInitialized(): boolean {
    return this.isInitialized;
  }

  /** Pure check — deliberately free of side effects so it is safe on any code path. */
  needsReauth(): boolean {
    if (this.sessionManagerService.getisIntegratedWithGoogle() !== 'true') {
      return false;
    }
    const expirationTimestamp = Number(localStorage.getItem('lastGeneratedAccessTokenExpires') || 0);
    return !this.sessionManagerService.getAccessToken() || expirationTimestamp < Date.now();
  }

  hasValidToken(): boolean {
    const expirationTimestamp = Number(localStorage.getItem('lastGeneratedAccessTokenExpires') || 0);
    return !!this.sessionManagerService.getAccessToken() && expirationTimestamp > Date.now();
  }

  /**
   * Renew the token if it has lapsed, choosing the means the platform allows.
   *
   * Safe to call from anywhere — visibility changes, page load, route guards,
   * the refresh timer. It never opens a pop-up where one would be blocked.
   */
  ensureFreshToken(): void {
    if (this.sessionManagerService.getisIntegratedWithGoogle() !== 'true') {
      // Integration was switched off — never leave a prompt stranded on screen.
      this.setReauthNeeded(false);
      return;
    }
    if (this.hasValidToken()) {
      this.setReauthNeeded(false);
      this.tokenValiditySubject.next(true);
      return;
    }

    // iPadOS defers the pop-up past its gesture and hangs, so never open one
    // on its own here — ask for a tap.
    if (isTouchSafari()) {
      this.markReauthNeeded();
      return;
    }

    // Only while the app is actually on screen. A background refresh is what
    // used to surface Google windows to users who had switched away.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }

    this.requestTokenSilently();
  }

  /**
   * The ONLY place allowed to open a Google pop-up from a user gesture.
   *
   * Must be called synchronously from the tap. Do not `await` anything first —
   * in Safari an intervening async operation ends the transient activation and
   * the pop-up is blocked.
   *
   * @param onSuccess work to run once the token has been renewed.
   */
  requestTokenFromUserGesture(onSuccess?: () => void): boolean {
    // A renewal is already running — queue the work onto it rather than
    // dropping it. This is the student who taps Save while the reconnect
    // prompt's own request is still open.
    if (this.requestInFlight) {
      this.queueAction(onSuccess);
      return true;
    }
    if (!this.ensureClient()) {
      this.markReauthNeeded();
      return false;
    }

    this.queueAction(onSuccess);
    return this.openTokenPopup();
  }

  /**
   * Login-time scope request, from the sign-in flow. Callers must not block
   * navigation on it — a refused pop-up fires `googleAuthFailCallback`.
   */
  integrateGoogleScopePermissions(): void {
    if (this.requestInFlight || !this.ensureClient()) {
      return;
    }
    this.openTokenPopup();
  }

  /** Background renewal, for platforms where a pop-up completes silently. */
  private requestTokenSilently(): void {
    if (this.requestInFlight || !this.ensureClient()) {
      return;
    }
    this.openTokenPopup();
  }

  /** Single point where the Google pop-up is actually opened. */
  private openTokenPopup(): boolean {
    this.beginRequest();
    try {
      this.client.requestAccessToken();
      return true;
    } catch (e) {
      this.endRequest();
      this.clearPendingActions();
      this.markReauthNeeded();
      return false;
    }
  }

  private ensureClient(): boolean {
    if (!this.client) {
      // Never awaited: doing so from a tap handler would lose the gesture.
      this.initializeAuth();
    }
    return !!this.client;
  }

  private startRefreshInterval(): void {
    clearInterval(this.refreshIntervalId);
    // `ensureFreshToken` is itself visibility- and platform-aware, so this
    // stays quiet in background tabs and on iPad.
    this.refreshIntervalId = setInterval(() => this.ensureFreshToken(), TOKEN_REFRESH_INTERVAL_MS);
  }

  /** Flags that a reconnect is required. Never opens a popup. */
  markReauthNeeded(): void {
    this.setReauthNeeded(true);
    this.tokenValiditySubject.next(false);
  }

  /**
   * Marks a popup as open and arms a safety release.
   */
  private beginRequest(): void {
    this.requestInFlight = true;
    clearTimeout(this.requestInFlightTimeoutId);
    this.requestInFlightTimeoutId = setTimeout(() => {
      if (this.requestInFlight) {
        this.endRequest();
        this.markReauthNeeded();
      }
    }, REQUEST_TIMEOUT_MS);
  }

  private endRequest(): void {
    this.requestInFlight = false;
    clearTimeout(this.requestInFlightTimeoutId);
    this.requestInFlightTimeoutId = undefined;
  }

  private setReauthNeeded(value: boolean): void {
    if (this.reauthNeededSubject.value !== value) {
      this.reauthNeededSubject.next(value);
    }
  }

  private queueAction(action?: () => void): void {
    if (action) {
      this.pendingActions.push(action);
    }
  }

  private runPendingActions(): void {
    const actions = this.pendingActions;
    this.pendingActions = [];
    actions.forEach(action => action());
  }

  private clearPendingActions(): void {
    this.pendingActions = [];
  }

  getCurrentUrl() {
    return this.router.url;
  }
}
